/**
 * Deterministic colour palette for site tiles. This mirrors the app switcher
 * org unit avatar: sum the display name's char codes, then pick from
 * [purple, blue, lime, yellow].
 *
 * Each entry holds both the token *paths* (for `@atlaskit/tile` / `@atlaskit/text`,
 * which resolve tokens internally) and the *resolved* CSS values (for places
 * that need raw colours). `token()` is only ever called with string literals so
 * static extraction is preserved.
 */
import { token } from '@atlaskit/tokens';
import type { TextColor } from '@atlaskit/primitives';
import type { TileProps } from '@atlaskit/tile';

export interface SiteTileColor {
  /** Token path for the tile background (for `@atlaskit/tile`). */
  backgroundToken: NonNullable<TileProps['backgroundColor']>;
  /** Token path for the tile/letter foreground (for `@atlaskit/text`). */
  foregroundToken: TextColor;
  /** Resolved background CSS value for styles config. */
  background: string;
  /** Resolved foreground CSS value for styles config. */
  foreground: string;
  /** Resolved accent border CSS value for styles config. */
  border: string;
}

/**
 * Accent colour entries keyed by Atlassian Design System hue. Each holds the
 * token paths and resolved CSS values. `token()` is called with string literals
 * only so static extraction is preserved.
 */
export const ACCENT = {
  red: {
    backgroundToken: 'color.background.accent.red.subtler',
    foregroundToken: 'color.text.accent.red.bolder',
    background: token('color.background.accent.red.subtler'),
    foreground: token('color.text.accent.red.bolder'),
    border: token('color.border.accent.red'),
  },
  yellow: {
    backgroundToken: 'color.background.accent.yellow.subtler',
    foregroundToken: 'color.text.accent.yellow.bolder',
    background: token('color.background.accent.yellow.subtler'),
    foreground: token('color.text.accent.yellow.bolder'),
    border: token('color.border.accent.yellow'),
  },
  lime: {
    backgroundToken: 'color.background.accent.lime.subtler',
    foregroundToken: 'color.text.accent.lime.bolder',
    background: token('color.background.accent.lime.subtler'),
    foreground: token('color.text.accent.lime.bolder'),
    border: token('color.border.accent.lime'),
  },
  green: {
    backgroundToken: 'color.background.accent.green.subtler',
    foregroundToken: 'color.text.accent.green.bolder',
    background: token('color.background.accent.green.subtler'),
    foreground: token('color.text.accent.green.bolder'),
    border: token('color.border.accent.green'),
  },
  teal: {
    backgroundToken: 'color.background.accent.teal.subtler',
    foregroundToken: 'color.text.accent.teal.bolder',
    background: token('color.background.accent.teal.subtler'),
    foreground: token('color.text.accent.teal.bolder'),
    border: token('color.border.accent.teal'),
  },
  blue: {
    backgroundToken: 'color.background.accent.blue.subtler',
    foregroundToken: 'color.text.accent.blue.bolder',
    background: token('color.background.accent.blue.subtler'),
    foreground: token('color.text.accent.blue.bolder'),
    border: token('color.border.accent.blue'),
  },
  purple: {
    backgroundToken: 'color.background.accent.purple.subtler',
    foregroundToken: 'color.text.accent.purple.bolder',
    background: token('color.background.accent.purple.subtler'),
    foreground: token('color.text.accent.purple.bolder'),
    border: token('color.border.accent.purple'),
  },
  gray: {
    backgroundToken: 'color.background.accent.gray.subtler',
    foregroundToken: 'color.text.accent.gray.bolder',
    background: token('color.background.accent.gray.subtler'),
    foreground: token('color.text.accent.gray.bolder'),
    border: token('color.border.accent.gray'),
  },
} satisfies Record<string, SiteTileColor>;

/** App switcher name-hash palette — order is significant. */
const SITE_TILE_COLORS: readonly SiteTileColor[] = [
  ACCENT.purple,
  ACCENT.blue,
  ACCENT.lime,
  ACCENT.yellow,
];

const getColorIndex = (value: string, paletteLength: number): number => {
  const charCodeSum = value.split('').reduce((sum, char) => {
    return sum + char.charCodeAt(0);
  }, 0);

  return charCodeSum % paletteLength;
};

/**
 * Returns the colour entry for a given site value. Empty values fall back to
 * the first app switcher palette entry.
 */
export const getSiteTileColor = (value: string): SiteTileColor =>
  SITE_TILE_COLORS[getColorIndex(value, SITE_TILE_COLORS.length)];

/** Maps each site avatar icon name to its background accent colour. */
const AVATAR_ICON_COLOR: Readonly<Record<string, SiteTileColor>> = {
  compass: ACCENT.red,
  cup: ACCENT.yellow,
  flag: ACCENT.yellow,
  triangle: ACCENT.yellow,
  rings: ACCENT.green,
  trophy: ACCENT.green,
  globe: ACCENT.teal,
  lightbulb: ACCENT.teal,
  pencilmarker: ACCENT.teal,
  site: ACCENT.teal,
  koala: ACCENT.blue,
  star: ACCENT.blue,
  jersey: ACCENT.purple,
  rocket: ACCENT.purple,
  invalid: ACCENT.gray,
};

const PNG_EXTENSION = '.png';

/**
 * Extracts the bare icon name from an avatar URL, e.g.
 * `https://.../avatars/240/koala.png` → `koala`. Returns `undefined` when the
 * value is not a valid URL ending in `<name>.png`.
 */
export const parseAvatarIconName = (
  avatarUrl: string | undefined,
): string | undefined => {
  if (!avatarUrl) {
    return undefined;
  }

  let pathname: string;
  try {
    pathname = new URL(avatarUrl).pathname;
  } catch {
    return undefined;
  }

  const fileName = pathname.split('/').pop()?.toLowerCase() ?? '';
  if (!fileName.endsWith(PNG_EXTENSION)) {
    return undefined;
  }

  return fileName.slice(0, -PNG_EXTENSION.length) || undefined;
};

/**
 * Returns the accent colour matching a site avatar icon's background, or
 * `undefined` when the URL is missing or the icon is not in the known library.
 */
export const getAvatarIconColor = (
  avatarUrl: string | undefined,
): SiteTileColor | undefined => {
  const name = parseAvatarIconName(avatarUrl);
  return name ? AVATAR_ICON_COLOR[name] : undefined;
};
